/**
 * KİMLİK EKRANI — Worker modunda giriş / davetli kayıt.
 *
 * İKİ KURAL:
 *   1. Yerel modda bu ekran HİÇ görünmez. `backend === 'local'` ise uygulama
 *      doğrudan açılır; hesap yoktur.
 *   2. Davet kodu YALNIZCA sahip (owner) üretir. Kayıt ekranı kod ister.
 *
 * Kayıt akışı: Arkadaşın davet bağlantısını açar → kod ekrana
 * önceden doldurulur → e-posta/parola seçer → hazır.
 */

import type { AuthState, InviteSummary, Repository } from '@tttrpg/db/client'
import { useCallback, useEffect, useState } from 'react'

/**
 * Uygulama adı — `VITE_APP_NAME` ortam değişkeninden.
 *
 * Tanımlı değilse "Zarhane"ye düşer (geliştirme varsayılanı). Ad artık
 * "Hiçliğin Derinlikleri"; tek kaynak olsun diye her ekranda sabit yazı
 * tutmuyoruz. `VITE_APP_NAME` bir kez değişince giriş ekranı, üst çubuk,
 * sekme başlığı ve PWA adı birlikte değişir.
 */
const APP_NAME = import.meta.env.VITE_APP_NAME || 'Zarhane'

export interface AuthGateProps {
  repository: Repository
  auth: AuthState
  onAuthChange: () => void
  children: React.ReactNode
}

export function AuthGate({ repository, auth, onAuthChange, children }: AuthGateProps) {
  if (auth.status === 'unknown') return <Splash text="Bağlanılıyor…" />

  if (auth.status === 'signed-out') {
    return <AuthScreen repository={repository} onDone={onAuthChange} />
  }

  return <>{children}</>
}

// ---------------------------------------------------------------------------

function AuthScreen({ repository, onDone }: { repository: Repository; onDone: () => void }) {
  // Davet bağlantısındaki kod: ?davet=ABC-DEF-GHI
  const [inviteCode, setInviteCode] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('davet')?.toUpperCase() ?? ''
    } catch {
      return ''
    }
  })

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [mode, setMode] = useState<'sign-in' | 'sign-up'>('sign-in')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      setBusy(true)
      setError(null)

      const result =
        mode === 'sign-in'
          ? await repository.signIn(email, password)
          : await repository.signUp(email, password, {
              displayName: displayName || undefined,
              inviteCode: inviteCode || undefined,
            })

      setBusy(false)
      if (result.ok) {
        onDone()
      } else {
        setError(result.error)
      }
    },
    [mode, repository, email, password, displayName, inviteCode, onDone],
  )

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 px-5 py-10">
      <header className="text-center">
        {/* Uygulama adı tek kaynaktan: `VITE_APP_NAME`. */}
        <h1 className="font-display text-2xl tracking-[0.3em] text-gold uppercase">{APP_NAME}</h1>
        <p className="mt-2 text-sm text-ink-muted">
          Davetle girilen özel masa. Davet kodun yoksa birinden iste.
        </p>
      </header>

      <form onSubmit={submit} className="flex flex-col gap-3">
        {mode === 'sign-up' && (
          <Field
            label="Davet kodu"
            value={inviteCode}
            onChange={(value) => setInviteCode(value.toUpperCase())}
            placeholder="ABC-DEF-GHI"
            hint="Arkadaşının gönderdiği kod. Kodu bilmiyorsan bu ekranı kullanma."
          />
        )}

        {mode === 'sign-up' && (
          <Field
            label="Görünen ad"
            value={displayName}
            onChange={setDisplayName}
            placeholder="Kaelen"
          />
        )}

        <Field
          label="E-posta"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="sen@ornek.com"
          required
        />

        <Field
          label="Parola"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="••••••••"
          hint={mode === 'sign-up' ? 'En az 8 karakter.' : undefined}
          required
        />

        {error && (
          <p className="rounded border border-blood/40 bg-blood/10 px-3 py-2 text-xs text-blood-bright">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="cursor-pointer rounded border border-gold-dim bg-gold/10 px-3 py-2 font-display text-sm tracking-[0.2em] text-gold uppercase transition-colors hover:bg-gold/20 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Bekle…' : mode === 'sign-in' ? 'Giriş yap' : 'Kayıt ol'}
        </button>

        <button
          type="button"
          onClick={() => {
            setMode(mode === 'sign-in' ? 'sign-up' : 'sign-in')
            setError(null)
          }}
          className="cursor-pointer self-center text-xs text-ink-muted underline decoration-dotted hover:text-gold"
        >
          {mode === 'sign-in' ? 'Davet kodum var — kayıt ol' : 'Zaten kayıtlıyım — giriş yap'}
        </button>
      </form>
    </main>
  )
}

// ---------------------------------------------------------------------------
// DAVET YÖNETİMİ (yalnızca sahip)
// ---------------------------------------------------------------------------

export function InvitePanel({ repository }: { repository: Repository }) {
  const [invites, setInvites] = useState<InviteSummary[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [maxUses, setMaxUses] = useState(1)
  const [days, setDays] = useState(14)

  const appUrl = import.meta.env.VITE_APP_URL ?? window.location.origin

  const reload = useCallback(async () => {
    try {
      setInvites(await repository.listInvites())
      setError(null)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Davetler okunamadı.')
    }
  }, [repository])

  useEffect(() => {
    void reload()
  }, [reload])

  const create = useCallback(async () => {
    setBusy(true)
    try {
      await repository.createInvite({ maxUses, expiresInDays: days })
      await reload()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Davet oluşturulamadı.')
    } finally {
      setBusy(false)
    }
  }, [repository, maxUses, days, reload])

  const revoke = useCallback(
    async (code: string) => {
      try {
        await repository.revokeInvite(code)
        await reload()
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Davet iptal edilemedi.')
      }
    },
    [repository, reload],
  )

  return (
    <section className="flex flex-col gap-3 rounded border border-line-soft bg-surface-2 p-4">
      <h2 className="font-display text-sm tracking-[0.2em] text-gold uppercase">Davetler</h2>

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          <span>Kişi sayısı</span>
          <input
            type="number"
            min={1}
            max={50}
            value={maxUses}
            onChange={(event) => setMaxUses(Number(event.target.value))}
            className="tnum w-20 rounded border border-line-soft bg-surface px-2 py-1 text-ink"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          <span>Geçerlilik (gün)</span>
          <input
            type="number"
            min={1}
            max={90}
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            className="tnum w-20 rounded border border-line-soft bg-surface px-2 py-1 text-ink"
          />
        </label>

        <button
          type="button"
          onClick={() => void create()}
          disabled={busy}
          className="cursor-pointer rounded border border-gold-dim px-2 py-1 text-xs text-gold hover:bg-gold/10 disabled:opacity-50"
        >
          {busy ? '…' : 'Yeni davet'}
        </button>
      </div>

      {error && <p className="text-xs text-blood-bright">{error}</p>}

      <ul className="flex flex-col gap-2">
        {invites.map((invite) => (
          <li key={invite.code} className="flex flex-wrap items-center gap-2 text-xs">
            <code className="rounded bg-surface px-1.5 py-0.5 tracking-wider text-gold-bright">
              {invite.code}
            </code>
            <span className="tnum text-ink-faint">
              {invite.uses}/{invite.maxUses} kullanıldı
              {invite.expiresAt
                ? ` · ${new Date(invite.expiresAt).toLocaleDateString('tr-TR')}`
                : ''}
            </span>
            {invite.revoked || !invite.usable ? (
              <span className="text-ink-faint">geçersiz</span>
            ) : (
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(`${appUrl}/?davet=${invite.code}`)
                }}
                className="cursor-pointer text-ink-muted underline decoration-dotted hover:text-gold"
              >
                bağlantıyı kopyala
              </button>
            )}
            {!invite.revoked && (
              <button
                type="button"
                onClick={() => void revoke(invite.code)}
                className="ml-auto cursor-pointer text-ink-faint hover:text-blood"
              >
                iptal
              </button>
            )}
          </li>
        ))}
        {invites.length === 0 && <li className="text-xs text-ink-faint">Henüz davet yok.</li>}
      </ul>
    </section>
  )
}

// ---------------------------------------------------------------------------

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
  required,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  placeholder?: string
  hint?: string
  required?: boolean
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-ink-muted">
      <span>{label}</span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete={type === 'password' ? 'current-password' : 'off'}
        className="rounded border border-line-soft bg-surface px-2 py-1.5 text-ink placeholder:text-ink-faint"
      />
      {hint && <span className="text-[0.65rem] text-ink-faint">{hint}</span>}
    </label>
  )
}

export function Splash({ text }: { text: string }) {
  return (
    <main className="flex min-h-dvh items-center justify-center">
      <p className="font-display text-sm tracking-[0.3em] text-gold uppercase">{text}</p>
    </main>
  )
}
